import { useUpdateEffect } from '@innovamat/hooks';
import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { createContext, useCallback, useMemo, useReducer } from 'react';
import { useRosterStreamLogin } from '../hooks/use-roster-stream-login';
import { useUsersLogin } from '../hooks/useUsersLogin';
import { useUsersLoginWebview } from '../hooks/useUsersLoginWebview';
import authReducer, { initState } from '../state/reducer';
import type { AuthDispatch, AuthState } from '../state/types';
import type { Credentials } from '../types/credentials';
import type {
  Autologin,
  LoginHookProps,
  LoginMethods,
  LoginRequest,
  RosterStreamProvider,
} from '../types/login';
import type { DecodedTokens } from '../types/token';
import { decodeTodenData } from '../utils/decode';

type Props = {
  children: React.ReactNode;
  autologin?: Autologin;
  credentials: Credentials;
};

type AuthProviderValue = AuthState & {
  isLoggedIn: boolean;
  onLogin: (type: LoginMethods, payload?: LoginRequest | string) => void;
  getDecodedTokenInfo: () => DecodedTokens | undefined;
  autologin?: Autologin;
  credentials: Credentials;
};

export const AuthStateContext = createContext<AuthProviderValue | undefined>(
  undefined
);

export const AuthDispatchContext = createContext<AuthDispatch | undefined>(
  undefined
);

function LoginProvider({ children, autologin, credentials }: Props) {
  const [state, dispatch] = useReducer(authReducer, {
    ...initState,
  });

  const getProps = (loginMethod: LoginMethods): LoginHookProps => {
    return {
      dispatch,
      credentials,
      autologin: autologin?.[loginMethod.type],
    };
  };

  const onLoginUsers = useUsersLogin(getProps({ type: 'users' }));
  const onLoginUsersWebview = useUsersLoginWebview(
    getProps({ type: 'login_from_webview' })
  );

  const onLoginRosterStream = useRosterStreamLogin({
    ...getProps({ type: 'roster_stream' }),
    rosterStreamAppId: credentials.rosterStreamAppId || '',
  });

  const onLogin = useCallback(
    (loginMethod: LoginMethods) => {
      if (loginMethod.type === 'users' && loginMethod.payload) {
        onLoginUsers(loginMethod.payload as LoginRequest);
      }

      if (loginMethod.type === 'roster_stream') {
        onLoginRosterStream(
          loginMethod.payload?.provider as RosterStreamProvider
        );
      }

      if (
        loginMethod.type === 'login_from_webview' &&
        loginMethod.payload &&
        typeof loginMethod.payload === 'string'
      ) {
        onLoginUsersWebview(loginMethod.payload);
      }
    },
    [onLoginUsers, onLoginUsersWebview, onLoginRosterStream]
  );

  /* 
    After getting the response with the token, we clear the reducer to
    avoid having to maintain two providers with the same token

    Note: in the future this provider will be the only that has the token
  */

  useUpdateEffect(() => {
    if (state.loginResponse) {
      dispatch({ type: 'CLEAR' });
    }
  }, [state.loginResponse]);

  const getDecodedTokenInfo = useCallback(() => {
    if (state.loginResponse) {
      return decodeTodenData(state.loginResponse);
    }
    return undefined;
  }, [state.loginResponse]);

  const value: AuthProviderValue = useMemo(
    () => ({
      ...state,
      isLoggedIn: Boolean(state.loginResponse?.access_token),
      onLogin,
      getDecodedTokenInfo,
      credentials,
      autologin,
    }),
    [credentials, getDecodedTokenInfo, onLogin, state]
  );

  return (
    <AuthStateContext.Provider value={value}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
}

export function AuthProvider({ children, credentials, autologin }: Props) {
  return (
    <GoogleOAuthProvider clientId={credentials.googleClientId}>
      <LoginProvider credentials={credentials} autologin={autologin}>
        {children}
      </LoginProvider>
    </GoogleOAuthProvider>
  );
}
